import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import type { ProspectSource } from "../../api/client.ts";
import { hitKindIcon, sourceLabel } from "./hitKinds.tsx";

/**
 * What prospected the company, as compact chips: "⚡ 2" for the signals of a
 * workflow, the agent's name ×n for a prospecting agent, a person for adds by
 * hand. `dense` (a table cell) shows counts only and names on hover.
 */
export default function ProspectSourcesCell({
  sources,
  dense = false,
}: {
  sources: ProspectSource[] | undefined;
  dense?: boolean;
}) {
  if (!sources?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
      {sources.map((source, i) => {
        const label = dense
          ? source.kind === "agent"
            ? `${shortName(source.agent_name ?? "Agent")} ×${source.count}`
            : source.kind === "signal" && source.workflow_name
              ? `${source.count} · ${shortName(source.workflow_name)}`
              : String(source.count)
          : sourceLabel(source);
        return (
          <Tooltip key={`${source.kind}-${source.workflow_id ?? source.agent_definition_id ?? i}`} title={sourceLabel(source)}>
            <Chip
              size="small"
              variant="outlined"
              color={source.kind === "signal" ? "warning" : source.kind === "agent" ? "primary" : "default"}
              icon={hitKindIcon(source.kind, { fontSize: "small" })}
              label={label}
              sx={{ "& .MuiChip-label": { fontVariantNumeric: "tabular-nums" } }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
}

/** A name cut to fit a dense chip ("Prospecting agent" → "Prospecting", "Industrial incidents" → "Industrial inc…"). */
function shortName(name: string): string {
  const words = name.split(/\s+/);
  const first = words[0];
  if (words.length === 1 || first.length >= 10) return first.length > 14 ? `${first.slice(0, 12)}…` : first;
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}
